import type { Dimensions, Point, StrokePoint, ViewportTransform } from "./canvasTypes";

export const MIN_ZOOM = 0.05;
export const MAX_ZOOM = 8;

export function clamp(value: number, minimum: number, maximum: number): number {
  if (!Number.isFinite(value)) return minimum;
  return Math.min(maximum, Math.max(minimum, value));
}

export function clampZoom(zoom: number): number {
  return clamp(zoom, MIN_ZOOM, MAX_ZOOM);
}

export function distanceBetween(first: Point, second: Point): number {
  return Math.hypot(second.x - first.x, second.y - first.y);
}

export function midpoint(first: Point, second: Point): Point {
  return {
    x: (first.x + second.x) / 2,
    y: (first.y + second.y) / 2,
  };
}

export function screenToDocument(point: Point, viewport: ViewportTransform): Point {
  return {
    x: (point.x - viewport.panX) / viewport.zoom,
    y: (point.y - viewport.panY) / viewport.zoom,
  };
}

export function documentToScreen(point: Point, viewport: ViewportTransform): Point {
  return {
    x: point.x * viewport.zoom + viewport.panX,
    y: point.y * viewport.zoom + viewport.panY,
  };
}

export function fitToScreen(
  documentSize: Dimensions,
  viewportSize: Dimensions,
  padding = 32,
): ViewportTransform {
  const usableWidth = Math.max(1, viewportSize.width - padding * 2);
  const usableHeight = Math.max(1, viewportSize.height - padding * 2);
  const zoom = clampZoom(
    Math.min(usableWidth / documentSize.width, usableHeight / documentSize.height),
  );

  return {
    zoom,
    panX: (viewportSize.width - documentSize.width * zoom) / 2,
    panY: (viewportSize.height - documentSize.height * zoom) / 2,
  };
}

export function panViewport(viewport: ViewportTransform, delta: Point): ViewportTransform {
  return {
    ...viewport,
    panX: viewport.panX + delta.x,
    panY: viewport.panY + delta.y,
  };
}

export function zoomViewportAt(
  viewport: ViewportTransform,
  requestedZoom: number,
  screenAnchor: Point,
): ViewportTransform {
  const documentAnchor = screenToDocument(screenAnchor, viewport);
  const zoom = clampZoom(requestedZoom);

  return {
    zoom,
    panX: screenAnchor.x - documentAnchor.x * zoom,
    panY: screenAnchor.y - documentAnchor.y * zoom,
  };
}

export function viewportFromGesture(
  startViewport: ViewportTransform,
  startCentre: Point,
  currentCentre: Point,
  scaleRatio: number,
): ViewportTransform {
  const documentAnchor = screenToDocument(startCentre, startViewport);
  const zoom = clampZoom(startViewport.zoom * scaleRatio);

  return {
    zoom,
    panX: currentCentre.x - documentAnchor.x * zoom,
    panY: currentCentre.y - documentAnchor.y * zoom,
  };
}

export function constrainViewport(
  viewport: ViewportTransform,
  documentSize: Dimensions,
  viewportSize: Dimensions,
  visibleMargin = 48,
): ViewportTransform {
  const scaledWidth = documentSize.width * viewport.zoom;
  const scaledHeight = documentSize.height * viewport.zoom;

  const constrainAxis = (pan: number, scaledSize: number, viewportLength: number) => {
    if (scaledSize <= viewportLength) return (viewportLength - scaledSize) / 2;
    return clamp(pan, visibleMargin - scaledSize, viewportLength - visibleMargin);
  };

  return {
    ...viewport,
    panX: constrainAxis(viewport.panX, scaledWidth, viewportSize.width),
    panY: constrainAxis(viewport.panY, scaledHeight, viewportSize.height),
  };
}

export function clampPointToDocument(point: Point, documentSize: Dimensions): Point {
  return {
    x: clamp(point.x, 0, documentSize.width),
    y: clamp(point.y, 0, documentSize.height),
  };
}

export function isPointInsideDocument(point: Point, documentSize: Dimensions): boolean {
  return (
    point.x >= 0 && point.y >= 0 && point.x <= documentSize.width && point.y <= documentSize.height
  );
}

export function interpolateStrokePoints(
  from: StrokePoint,
  to: StrokePoint,
  maximumSpacing: number,
): StrokePoint[] {
  const distance = distanceBetween(from, to);
  if (distance === 0) return [];

  const steps = Math.max(1, Math.ceil(distance / Math.max(0.25, maximumSpacing)));
  const points: StrokePoint[] = [];

  for (let step = 1; step <= steps; step += 1) {
    const ratio = step / steps;
    points.push({
      x: from.x + (to.x - from.x) * ratio,
      y: from.y + (to.y - from.y) * ratio,
      pressure: from.pressure + (to.pressure - from.pressure) * ratio,
    });
  }

  return points;
}
